import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import Button from '../../components/Button';
import { Target } from 'lucide-react';
import OnboardingProgress from '../../components/OnboardingProgress';

const schema = z.object({
  goals: z.string().min(10, 'Please tell us more about your goals'),
});

type FormData = z.infer<typeof schema>;

export default function OnboardingGoals() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuthStore();
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      goals: user?.goals || '',
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await updateProfile({
        goals: data.goals,
      });
      navigate('/onboarding/terms');
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8">
      <OnboardingProgress currentStep="goals" />
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl text-center font-bold font-dillan text-primary mb-2">
          What brings you to our platform?
        </h1>
        <p className="text-neutral text-center text-l mb-6">
          Tell us about your goals and what you hope to achieve here.
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <textarea
              id="goals"
              {...register('goals')}
              className="w-full h-40 pl-8 px-2 py-8 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m mx-auto block"
              placeholder="Share your aspirations and goals in the music industry. This will help us connect you with like-minded and valued people."
            />
            {errors.goals && (
              <p className="mt-1 pl-6 text-sm text-primary">{errors.goals.message}</p>
            )}
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 text-surface-light mt-8"
              onClick={() => navigate('/onboarding/experience')}
            >
              Back
            </Button>
            <Button type="submit" variant="primary" className="flex-1 text-surface-light mt-8">
              Continue
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}